import React, { PropsWithChildren } from "react";
import { Dropdown } from "react-bootstrap";
import { FiMoreVertical } from "react-icons/fi";
import type { TableActionButtonProps } from "./TableActionButton";

export interface TableActionsProps {
  /** Accessible group label, e.g. `Actions for church Bethel`. */
  "aria-label"?: string;
  /**
   * Max number of `primary` actions shown as labeled buttons on desktop (≥ md).
   * Defaults to 2. Raise (e.g. to 3) only on sparse tables whose non-action
   * content columns leave room for an extra labeled button; mobile still
   * collapses every action into the ⋮ kebab.
   */
  maxInline?: number;
}

// react-bootstrap's `Dropdown` trips its generic children typing under React 19
// (Toggle + Menu read as "multiple children"); alias to a loose type so it
// type-checks. Runtime is identical to `Dropdown`.
const Dd = Dropdown as unknown as React.FC<React.PropsWithChildren<Record<string, any>>>;

/** Render the icon for a child action, supporting both string (Bootstrap) and node icons. */
const renderIcon = (icon: React.ReactNode): React.ReactNode =>
  typeof icon === "string" ? <i className={`bi bi-${icon}`} aria-hidden /> : icon;

/** Map a child's hover-colour modifier class to a dropdown-item text colour. */
const itemColorClass = (className?: string): string => {
  if (!className) return "";
  if (className.includes("--danger")) return "text-danger";
  if (className.includes("--success")) return "text-success";
  return "";
};

type ActionItem = React.ReactElement<TableActionButtonProps>;

/** A ⋮ kebab dropdown listing the given actions as icon + label menu items. */
const renderKebab = (items: ActionItem[], ariaLabel: string | undefined, className: string) => (
  <Dd className={className} align="end">
    <Dropdown.Toggle
      variant="outline-secondary"
      size="sm"
      aria-label={ariaLabel ?? "Actions"}
      className="ntcog-table-action-btn ntcog-table-actions-kebab"
    >
      <FiMoreVertical aria-hidden />
    </Dropdown.Toggle>
    <Dropdown.Menu
      renderOnMount
      popperConfig={{
        strategy: "fixed",
        modifiers: [
          { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
        ],
      }}
    >
      {items.map((child, index) => {
        const { icon, label, onClick, disabled, className: itemClass } = child.props;
        return (
          <Dropdown.Item
            key={child.key ?? index}
            onClick={onClick}
            disabled={disabled}
            className={`d-flex align-items-center gap-2${
              itemColorClass(itemClass) ? ` ${itemColorClass(itemClass)}` : ""
            }`}
          >
            {renderIcon(icon)}
            <span>{label}</span>
          </Dropdown.Item>
        );
      })}
    </Dropdown.Menu>
  </Dd>
);

const DEFAULT_MAX_INLINE = 2;

/**
 * Responsive wrapper for table-row actions.
 *
 * - Desktop (≥ md): shows up to `maxInline` (default 2) `primary`-flagged
 *   actions as icon + text buttons; every other action (and all un-flagged
 *   ones) collapses into a ⋮ overflow menu so wide rows never force
 *   horizontal scroll.
 * - Mobile (< md): collapses ALL actions into a single ⋮ dropdown.
 *
 * Both layouts are driven by the same children — conditional JSX such as
 * `{row.is_active ? <Disable/> : <Enable/>}` works unchanged.
 */
const TableActions: React.FC<PropsWithChildren<TableActionsProps>> = ({
  children,
  "aria-label": ariaLabel,
  maxInline = DEFAULT_MAX_INLINE,
}) => {
  // toArray drops null/false/undefined from conditional rendering and keys items.
  const items = React.Children.toArray(children).filter(
    React.isValidElement,
  ) as ActionItem[];

  if (items.length === 0) return null;

  // Inline: up to maxInline actions explicitly flagged primary. Destructive /
  // status actions carry a colour modifier class and are never eligible inline,
  // even if mistakenly flagged.
  const isInlineEligible = (el: ActionItem) =>
    el.props.primary === true && !itemColorClass(el.props.className);
  const inlineItems = items.filter(isInlineEligible).slice(0, maxInline);
  const overflowItems = items.filter((el) => !inlineItems.includes(el));

  return (
    <>
      {/* Desktop / tablet: up to maxInline buttons + overflow ⋮ */}
      <div
        className="ntcog-table-actions d-none d-md-inline-flex"
        role="group"
        aria-label={ariaLabel}
      >
        {inlineItems.map((child, index) =>
          React.cloneElement(child, { key: child.key ?? index, showText: true }),
        )}
        {overflowItems.length > 0 && renderKebab(overflowItems, ariaLabel, "d-inline-flex")}
      </div>

      {/* Mobile: single kebab dropdown with all actions */}
      {renderKebab(items, ariaLabel, "d-inline-flex d-md-none")}
    </>
  );
};

export default TableActions;
